import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { z } from "zod";
import { requireActiveAuth as requireSupabaseAuth } from "@/lib/auth/requireActiveAuth";
import { COMPLIANCE_MODE } from "@/config/compliance";
import { captureMisconfiguredResource } from "@/lib/libraryMisconfig.server";

export type FeaturedLeadMagnet = {
  id: string;
  title: string;
  description: string | null;
  headline: string;
  subhead: string;
  category: string;
  cover_image_url: string | null;
  download_count: number;
};

/** Public: the single featured lead magnet shown on the Academy + Library. */
export const getFeaturedLeadMagnet = createServerFn({ method: "GET" }).handler(
  async (): Promise<FeaturedLeadMagnet | null> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("family_library")
      .select(
        "id, title, description, category, cover_image_url, thumbnail_url, download_count, lead_magnet_headline, lead_magnet_subhead",
      )
      .eq("published", true)
      .eq("is_lead_magnet", true)
      .order("sort_order")
      .limit(1)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) return null;
    return {
      id: data.id,
      title: data.title,
      description: data.description,
      headline: data.lead_magnet_headline?.trim() || data.title,
      subhead:
        data.lead_magnet_subhead?.trim() ||
        data.description ||
        "A practical guide for families building generational wealth.",
      category: data.category,
      cover_image_url: data.cover_image_url ?? data.thumbnail_url ?? null,
      download_count: data.download_count ?? 0,
    };
  },
);

const requestSchema = z.object({
  resourceId: z.string().uuid(),
  email: z.string().trim().toLowerCase().email().max(254),
  firstName: z.string().trim().max(80).optional().or(z.literal("")),
  consentMarketing: z.boolean(),
  // honeypot — must be empty
  website: z.string().max(0).optional().or(z.literal("")),
});

function clientIp(): string | null {
  const fwd =
    getRequestHeader("cf-connecting-ip") ||
    getRequestHeader("x-forwarded-for") ||
    getRequestHeader("x-real-ip") ||
    "";
  const first = fwd.split(",")[0]?.trim();
  return first ? first : null;
}

async function unsubscribeToken(email: string) {
  const { signEmailToken } = await import("./shareToken.server");
  return signEmailToken(email);
}

/** Public: capture an email, then hand back a short-lived signed download URL. */
export const requestLeadMagnet = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => requestSchema.parse(d))
  .handler(async ({ data }) => {
    if (data.website) throw new Error("Request could not be processed.");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const ip = clientIp();
    const email = data.email.toLowerCase();

    // ---- rate limiting: 3 / IP / hour, 5 / email / day ----
    const hourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
    const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

    if (ip) {
      const { count } = await supabaseAdmin
        .from("lead_magnet_requests")
        .select("id", { count: "exact", head: true })
        .eq("ip", ip)
        .gte("created_at", hourAgo);
      if ((count ?? 0) >= 3) {
        throw new Error("Too many requests. Please try again in an hour.");
      }
    }
    const { count: emailCount } = await supabaseAdmin
      .from("lead_magnet_requests")
      .select("id", { count: "exact", head: true })
      .eq("email", email)
      .gte("created_at", dayAgo);
    if ((emailCount ?? 0) >= 5) {
      throw new Error("This email has reached today's download limit.");
    }
    await supabaseAdmin.from("lead_magnet_requests").insert({ ip, email });

    // ---- resource ----
    const { data: row, error } = await supabaseAdmin
      .from("family_library")
      .select("id, title, file_path, published, is_lead_magnet")
      .eq("id", data.resourceId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row || !row.published || !row.is_lead_magnet) {
      throw new Error("This resource is not available.");
    }

    if (!row.file_path) {
      captureMisconfiguredResource(row.id, row.title);
      throw new Error("This resource is not yet available.");
    }
    const { data: signed, error: signErr } = await supabaseAdmin.storage
      .from("family-library-files")
      .createSignedUrl(row.file_path, 60 * 60);
    if (signErr || !signed?.signedUrl) {
      throw new Error("Could not prepare your download. Please try again.");
    }
    const url = signed.signedUrl;

    // ---- subscriber ----
    const now = new Date().toISOString();
    const { data: existing } = await supabaseAdmin
      .from("education_subscribers")
      .select("id, consent_marketing")
      .eq("email", email)
      .maybeSingle();

    let subscriberId = existing?.id ?? null;
    if (subscriberId) {
      await supabaseAdmin
        .from("education_subscribers")
        .update({
          first_name: data.firstName || undefined,
          consent_marketing: data.consentMarketing || existing?.consent_marketing || false,
          consent_timestamp: data.consentMarketing ? now : undefined,
          consent_ip: data.consentMarketing ? ip : undefined,
          unsubscribed_at: data.consentMarketing ? null : undefined,
        })
        .eq("id", subscriberId);
    } else {
      const { data: ins, error: insErr } = await supabaseAdmin
        .from("education_subscribers")
        .insert({
          email,
          first_name: data.firstName || null,
          source_resource_id: row.id,
          consent_marketing: data.consentMarketing,
          consent_timestamp: data.consentMarketing ? now : null,
          consent_ip: data.consentMarketing ? ip : null,
        })
        .select("id")
        .single();
      if (insErr) throw new Error(insErr.message);
      subscriberId = ins.id;
    }

    await supabaseAdmin.from("lead_magnet_downloads").insert({
      subscriber_id: subscriberId,
      resource_id: row.id,
      user_agent: getRequestHeader("user-agent") ?? null,
    });
    await supabaseAdmin.rpc("increment_library_download", { _id: row.id });
    await supabaseAdmin.from("activity_logs").insert({
      action: "lead_magnet.downloaded",
      target_type: "family_library",
      target_id: row.id,
      metadata: { title: row.title, consent_marketing: data.consentMarketing },
    });

    // ---- delivery email (best effort) ----
    let emailed = false;
    try {
      emailed = await sendLeadMagnetEmail({
        to: email,
        firstName: data.firstName || null,
        title: row.title,
        url,
        token: await unsubscribeToken(email),
      });
    } catch {
      emailed = false;
    }

    return { url, emailed, title: row.title };
  });

async function sendLeadMagnetEmail(args: {
  to: string;
  firstName: string | null;
  title: string;
  url: string;
  token: string;
}): Promise<boolean> {
  const key = process.env["RESEND_API_KEY"];
  if (!key) return false;
  const from =
    process.env["EMAIL_FROM_DEFAULT"] ||
    "Currency Thoughts <hello@currencythoughts.net>";
  const site = "https://currencythoughts.net";
  const unsub = `${site}/unsubscribe?email=${encodeURIComponent(args.to)}&token=${args.token}`;
  const greeting = args.firstName ? `${args.firstName},` : "Hello,";
  const bookBlock = COMPLIANCE_MODE
    ? ""
    : `<p style="margin:24px 0 0"><a href="${site}/book" style="background:#A88B5C;color:#0A3369;padding:12px 20px;text-decoration:none;font-weight:600;letter-spacing:.05em">BOOK A STRATEGIST</a></p>`;

  const html = `
  <div style="background:#F5F1E8;padding:32px;font-family:Inter,Arial,sans-serif;color:#1F2937">
    <div style="max-width:560px;margin:0 auto;background:#fff;border:1px solid rgba(168,139,92,.25)">
      <div style="background:#002D68;color:#F5F1E8;padding:24px 28px">
        <div style="font-size:11px;letter-spacing:.22em;text-transform:uppercase;color:#C9A876">The Family Library</div>
        <div style="font-family:Georgia,serif;font-size:24px;margin-top:8px">Your guide is ready</div>
      </div>
      <div style="padding:28px">
        <p style="margin:0 0 12px">${greeting}</p>
        <p style="margin:0 0 20px;line-height:1.65">Thank you for requesting <strong>${args.title}</strong>. Your download link is below and stays active for 60 minutes.</p>
        <p style="margin:0"><a href="${args.url}" style="background:#0A3369;color:#F5F1E8;padding:12px 20px;text-decoration:none;font-weight:600;letter-spacing:.05em">DOWNLOAD THE GUIDE</a></p>
        ${bookBlock}
        <hr style="border:0;border-top:1px solid rgba(168,139,92,.4);margin:28px 0" />
        <p style="font-size:12px;line-height:1.6;color:#6B7280;margin:0">Currency Thoughts LLC is an independent insurance agency. Products and availability vary by state. All policy benefits are governed by the actual policy contract issued by the insurance carrier. Nothing in this email constitutes legal, tax, or investment advice. Materials are educational only.</p>
        <p style="font-size:12px;color:#6B7280;margin:12px 0 0"><a href="${unsub}" style="color:#A88B5C">Unsubscribe</a></p>
      </div>
    </div>
  </div>`;

  const res = await fetch("https://api.resend.com/emails", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${key}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      from,
      to: [args.to],
      subject: `Your download: ${args.title}`,
      html,
    }),
  });
  return res.ok;
}

/** Public: one-click unsubscribe from education emails. */
export const unsubscribeEducation = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        email: z.string().trim().toLowerCase().email().max(254),
        token: z.string().trim().min(10).max(64),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const expected = await unsubscribeToken(data.email);
    if (expected !== data.token) throw new Error("This unsubscribe link is invalid.");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin
      .from("education_subscribers")
      .update({ unsubscribed_at: new Date().toISOString(), consent_marketing: false })
      .eq("email", data.email);
    // Suppression list read by the send-email function before any marketing send.
    await supabaseAdmin
      .from("email_unsubscribes")
      .upsert(
        { email: data.email, source: "unsubscribe_link" },
        { onConflict: "email" },
      );
    return { ok: true };
  });

/**
 * Public: no-token fallback. Never reveals whether the address is on a list —
 * always returns { ok: true }. If the address IS known, we mail a freshly
 * minted token link; clicking it is what actually completes the unsubscribe.
 */
export const requestUnsubscribeLink = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        email: z.string().trim().toLowerCase().email().max(254),
        website: z.string().max(0).optional().or(z.literal("")),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    if (data.website) return { ok: true as const };
    const email = data.email.toLowerCase();
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

      // Rate limit: 3 confirmation mails / email / hour.
      const hourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      const { count } = await supabaseAdmin
        .from("lead_magnet_requests")
        .select("id", { count: "exact", head: true })
        .eq("email", `unsub:${email}`)
        .gte("created_at", hourAgo);
      if ((count ?? 0) >= 3) return { ok: true as const };
      await supabaseAdmin
        .from("lead_magnet_requests")
        .insert({ ip: clientIp(), email: `unsub:${email}` });

      const { data: sub } = await supabaseAdmin
        .from("education_subscribers")
        .select("id")
        .eq("email", email)
        .maybeSingle();
      if (!sub) return { ok: true as const };

      await sendUnsubscribeConfirmationEmail(email, await unsubscribeToken(email));
    } catch {
      // Swallow — the response must be identical either way.
    }
    return { ok: true as const };
  });

async function sendUnsubscribeConfirmationEmail(to: string, token: string) {
  const key = process.env["RESEND_API_KEY"];
  if (!key) return;
  const from =
    process.env["EMAIL_FROM_DEFAULT"] ||
    "Currency Thoughts <hello@currencythoughts.net>";
  const site = "https://currencythoughts.net";
  const link = `${site}/unsubscribe?email=${encodeURIComponent(to)}&token=${token}`;
  const html = `
  <div style="background:#F5F1E8;padding:32px;font-family:Inter,Arial,sans-serif;color:#1F2937">
    <div style="max-width:560px;margin:0 auto;background:#fff;border:1px solid rgba(168,139,92,.25)">
      <div style="background:#002D68;color:#F5F1E8;padding:24px 28px">
        <div style="font-size:11px;letter-spacing:.22em;text-transform:uppercase;color:#C9A876">Email Preferences</div>
        <div style="font-family:Georgia,serif;font-size:24px;margin-top:8px">Confirm your unsubscribe</div>
      </div>
      <div style="padding:28px">
        <p style="margin:0 0 20px;line-height:1.65">Someone asked to remove <strong>${to}</strong> from Currency Thoughts educational emails. Confirm below and we'll stop sending. If this wasn't you, ignore this message — nothing changes.</p>
        <p style="margin:0"><a href="${link}" style="background:#0A3369;color:#F5F1E8;padding:12px 20px;text-decoration:none;font-weight:600;letter-spacing:.05em">CONFIRM UNSUBSCRIBE</a></p>
        <p style="font-size:12px;color:#6B7280;margin:20px 0 0">To unsubscribe, click here or email currencythoughtsllc@gmail.com.</p>
      </div>
    </div>
  </div>`;
  await fetch("https://api.resend.com/emails", {
    method: "POST",
    headers: { Authorization: `Bearer ${key}`, "Content-Type": "application/json" },
    body: JSON.stringify({
      from,
      to: [to],
      subject: "Confirm your unsubscribe",
      html,
    }),
  });
}

// ---------------- Admin ----------------

async function assertAdmin(userId: string) {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin.rpc("is_admin", { _user_id: userId });
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden");
}

export type AdminSubscriberRow = {
  id: string;
  email: string;
  first_name: string | null;
  consent_marketing: boolean;
  unsubscribed_at: string | null;
  created_at: string;
  source_title: string | null;
  downloads: number;
};

export const adminListSubscribers = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<AdminSubscriberRow[]> => {
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("education_subscribers")
      .select(
        "id, email, first_name, consent_marketing, unsubscribed_at, created_at, family_library:source_resource_id (title), lead_magnet_downloads (id)",
      )
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);
    return (data ?? []).map((r: any) => ({
      id: r.id,
      email: r.email,
      first_name: r.first_name,
      consent_marketing: r.consent_marketing,
      unsubscribed_at: r.unsubscribed_at,
      created_at: r.created_at,
      source_title: r.family_library?.title ?? null,
      downloads: r.lead_magnet_downloads?.length ?? 0,
    }));
  });